/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationRemoteSessionClientsAttachKeySet } from "../funcs/organizationRemoteSessionClientsAttachKeySet.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RemoteSessionClient } from "../models/components/remotesessionclient.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  AttachOrganizationRemoteSessionClientKeySetRequest,
  AttachOrganizationRemoteSessionClientKeySetSecurity,
} from "../models/operations/attachorganizationremotesessionclientkeyset.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type AttachOrganizationRemoteSessionClientKeySetMutationVariables = {
  request: AttachOrganizationRemoteSessionClientKeySetRequest;
  security?: AttachOrganizationRemoteSessionClientKeySetSecurity | undefined;
  options?: RequestOptions;
};

export type AttachOrganizationRemoteSessionClientKeySetMutationData =
  RemoteSessionClient;

export type AttachOrganizationRemoteSessionClientKeySetMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * attachClientKeySet organizationRemoteSessionClients
 *
 * @remarks
 * Attach an organization JSON Web Key Set to a remote_session_client in the caller's organization, opting it into signing private_key_jwt assertions. Requires org:admin and the customer_managed_encryption_keys entitlement.
 */
export function useAttachOrganizationRemoteSessionClientKeySetMutation(
  options?: MutationHookOptions<
    AttachOrganizationRemoteSessionClientKeySetMutationData,
    AttachOrganizationRemoteSessionClientKeySetMutationError,
    AttachOrganizationRemoteSessionClientKeySetMutationVariables
  >,
): UseMutationResult<
  AttachOrganizationRemoteSessionClientKeySetMutationData,
  AttachOrganizationRemoteSessionClientKeySetMutationError,
  AttachOrganizationRemoteSessionClientKeySetMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildAttachOrganizationRemoteSessionClientKeySetMutation(
      client,
      options,
    ),
    ...options,
  });
}

export function mutationKeyAttachOrganizationRemoteSessionClientKeySet(): MutationKey {
  return ["@gram/client", "organizationRemoteSessionClients", "attachKeySet"];
}

export function buildAttachOrganizationRemoteSessionClientKeySetMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: AttachOrganizationRemoteSessionClientKeySetMutationVariables,
  ) => Promise<AttachOrganizationRemoteSessionClientKeySetMutationData>;
} {
  return {
    mutationKey: mutationKeyAttachOrganizationRemoteSessionClientKeySet(),
    mutationFn: function attachOrganizationRemoteSessionClientKeySetMutationFn({
      request,
      security,
      options,
    }): Promise<AttachOrganizationRemoteSessionClientKeySetMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(organizationRemoteSessionClientsAttachKeySet(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
